import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link2, Plus, Trash2 } from 'lucide-react';
import { Container, PageHeader, Section, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Card, CardHeader, CardTitle, CardBody, Skeleton, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, IconButton, Badge } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { usePeeringGroup, useVpcs, useAddVpcToGroup, useRemoveVpcFromGroup, type PeeringMember } from '@/api/opennebula';
import { DescList, DescItem, monoValue } from '../../components/DescList';
import { CopyIp } from './VpcDialogs';

export function PeeringDetail() {
  const { serviceId, groupId } = useParams<{ serviceId: string; groupId: string }>();
  const navigate = useNavigate();
  const peeringPath = `/services/compute/${serviceId}/vpc`;
  const { data: group, isLoading } = usePeeringGroup(serviceId, groupId);

  const crumbs = (current: string) => (
    <Breadcrumbs>
      <BreadcrumbItem href={peeringPath} onClick={(e) => { e.preventDefault(); navigate(peeringPath); }}>VPC</BreadcrumbItem>
      <BreadcrumbItem current>{current}</BreadcrumbItem>
    </Breadcrumbs>
  );

  if (isLoading || !group) {
    return (
      <Container size="xl">
        <PageHeader breadcrumbs={crumbs('Peering group')} icon={<Link2 size={22} />} title={<Skeleton style={{ height: 24, width: 200 }} />} />
        <Skeleton style={{ height: 160 }} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={crumbs(group.name || group.id)}
        icon={<Link2 size={22} />}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            {group.name || group.id}
            {group.status && <StatusPill tone={/active/i.test(group.status) ? 'success' : 'muted'}>{group.status}</StatusPill>}
          </span>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card flat>
          <CardHeader><CardTitle>Group information</CardTitle></CardHeader>
          <CardBody>
            <DescList>
              <DescItem label="Name">{group.name || '—'}</DescItem>
              <DescItem label="Peering subnet">{group.peering_subnet?.name || group.peering_subnet_id || '—'}</DescItem>
              <DescItem label="Status">{group.status || '—'}</DescItem>
              <DescItem label="Group ID"><code style={monoValue}>{group.id}</code></DescItem>
            </DescList>
          </CardBody>
        </Card>

        <Members serviceId={serviceId!} groupId={groupId!} members={group.members ?? []} />
      </div>
    </Container>
  );
}

function Members({ serviceId, groupId, members }: { serviceId: string; groupId: string; members: PeeringMember[] }) {
  const { show } = useToast();
  const { data: allVpcs = [] } = useVpcs(serviceId);
  const [pick, setPick] = useState('');
  const [removeTarget, setRemoveTarget] = useState<{ vpcId: string; name?: string } | null>(null);
  const memberVpcIds = new Set(members.map((m) => String(m.vpc_id)));
  const available = allVpcs.filter((v) => !memberVpcIds.has(String(v.id)));
  const vpcName = (vpcId: string) => allVpcs.find((v) => String(v.id) === String(vpcId))?.name;

  const add = useAddVpcToGroup(serviceId, groupId, { onSuccess: () => { show({ tone: 'success', title: 'VPC added' }); setPick(''); }, onError: () => show({ tone: 'danger', title: 'Add failed' }) });
  const remove = useRemoveVpcFromGroup(serviceId, groupId, { onSuccess: () => { show({ tone: 'success', title: 'VPC removed' }); setRemoveTarget(null); }, onError: () => show({ tone: 'danger', title: 'Remove failed' }) });

  const columns: ColumnDef<PeeringMember, unknown>[] = [
    { id: 'vpc', header: 'VPC', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.vpc_name || vpcName(row.original.vpc_id) || row.original.vpc_id}</span> },
    { id: 'interface_ip', header: 'Interface IP', cell: ({ row }) => { const ip = row.original.interface_ip || row.original.peering_ip; return ip ? <CopyIp ip={ip} /> : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>; } },
    { id: 'status', header: 'Status', cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.status || '—'}</Badge> },
    { id: 'actions', header: '', cell: ({ row }) => (
      <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton variant="ghost" size="sm" aria-label="Remove VPC" onClick={() => setRemoveTarget({ vpcId: String(row.original.vpc_id), name: row.original.vpc_name || vpcName(row.original.vpc_id) })}><Trash2 size={15} /></IconButton>
      </span>
    ) },
  ];

  return (
    <Section title="Member VPCs" description="VPCs connected through this peering group." style={{ marginBottom: 0 }}>
      <Card flat style={{ marginBottom: 'var(--vn-space-3)' }}>
        <CardBody style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
          <Select value={pick} onValueChange={setPick} disabled={available.length === 0}>
            <SelectTrigger style={{ flex: 1 }}><SelectValue placeholder={available.length ? 'Add a VPC' : 'No more VPCs to add'} /></SelectTrigger>
            <SelectContent>{available.map((v) => <SelectItem key={v.id} value={String(v.id)}>{v.name || v.id}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" startIcon={<Plus size={15} />} disabled={!pick || add.isPending} onClick={() => add.mutate(pick)}>Add VPC</Button>
        </CardBody>
      </Card>
      {/* DataTable carries its own bordered frame — don't wrap it in a Card (double border). */}
      <DataTable
        columns={columns}
        data={members}
        getRowId={(m) => String(m.vpc_id)}
        emptyState={<EmptyState icon={<Link2 size={24} />} title="No member VPCs" description="Add a VPC so it can route to the others in this group." />}
      />

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
        title="Remove VPC from peering group?"
        description={`“${removeTarget?.name || removeTarget?.vpcId}” will no longer route to the other members of this group.`}
        confirmLabel="Remove"
        loading={remove.isPending}
        onConfirm={() => removeTarget && remove.mutate(removeTarget.vpcId)}
      />
    </Section>
  );
}
